import { Component, effect, inject, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { TranslateService } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { ColorUtilitiesService } from '@utilities/color-utilities.service';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { GraphUtilitiesService } from '@utilities/graph-utilities.service';
import { NumberUtilitiesService } from '@utilities/number-utilities.service';
import { addDays, addHours, startOfDay, subHours } from 'date-fns';
import type { DataZoomComponentOption, EChartsOption, LineSeriesOption, SeriesOption } from 'echarts';
import type { ECElementEvent, EChartsType } from 'echarts/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { FindingSpansChart } from '../finding-spans-chart';
import { IFindingSpans, TGraphPoint } from '../graph-finding-spans.interface';
import { getCurveSegment, getFindingSpansLegend, sortFindingSpans } from '../graph-finding-spans.util';
import { getNextDotIndex, toDotIndex, toSeriesDataIndex } from '../graph-pair-mode.util';
import {
  IChartClickParams,
  ILineGraphData,
  ILineGraphInputData,
  TLineGraphSeriesItem,
  TMarkLineDataItem,
} from './line-graph.interface';

@Component({
  imports: [NgxEchartsDirective, MatButtonModule, IconComponent, NgxSkeletonLoaderModule],
  selector: 'app-line-graph',
  templateUrl: './line-graph.component.html',
})
export class LineGraphComponent {
  public readonly dotGraphData = input<ILineGraphInputData[]>([]);
  public readonly viewerGraphData = input<ILineGraphInputData[]>([]);
  public readonly pairMode = input<boolean>(false);
  // Replaces the dots: each finding drawn along the viewer line from start to end, with its number above.
  public readonly findingSpans = input<IFindingSpans | null>(null);

  public readonly pointSelected = output<string>();

  public readonly chartOption = signal<EChartsOption>({});
  public readonly chartFinished = signal(false);

  private readonly processedDotGraphData = signal<ILineGraphData[]>([]);
  private readonly processedViewerGraphData = signal<ILineGraphData[]>([]);

  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly numberUtils = inject(NumberUtilitiesService);
  private readonly translate = inject(TranslateService);
  private readonly graphUtilitiesService = inject(GraphUtilitiesService);
  private readonly findingSpansChart = new FindingSpansChart();

  private readonly viewersLabel = this.translate.instant('pages.campaignDetails.ctaAnalytics.details.graph.viewers');

  private chartInstance: EChartsType | null = null;
  private minTimestamp = 0;
  private maxTimestamp = 0;
  private minValue = 0;
  private maxValue = 0;
  private selectedPointDataIndex: number | null = null;

  constructor() {
    this.initializeChart();

    effect(() => {
      const dotGraphData = this.dotGraphData();
      const isPairMode = this.pairMode();

      const formattedDotGraphData = dotGraphData.map((item) => ({
        ...item,
        formattedTime: this.dateUtils.formatDate(new Date(item.xAxisValue), 'yyyy-MM-dd HH:mm'),
        status: item.status,
        timestamp: item.xAxisValue,
        value: item.yAxisValue,
      }));

      if (isPairMode && dotGraphData.length % 2 !== 0) {
        this.processedDotGraphData.set(formattedDotGraphData.slice(0, -1));
      } else {
        this.processedDotGraphData.set(formattedDotGraphData);
      }

      this.initializeChart();
    });

    effect(() => {
      const viewerGraphData = this.viewerGraphData();

      if (viewerGraphData?.length) {
        const interpolatedViewerGraphData = this.graphUtilitiesService.getInterpolatedViewerGraphData(viewerGraphData);
        const formattedViewerGraphData = interpolatedViewerGraphData.map((item) => ({
          ...item,
          formattedTime: this.dateUtils.formatDate(new Date(item.xAxisValue), 'yyyy-MM-dd HH:mm'),
          status: item.status,
          timestamp: item.xAxisValue,
          value: item.yAxisValue,
        }));

        this.processedViewerGraphData.set(formattedViewerGraphData);
      }
    });
  }

  public onChartInit(chart: EChartsType): void {
    this.chartInstance = chart;
    this.setupChartClickListener(chart);
    chart.on('rendered', () => this.findingSpansChart.updateLabels(chart));
  }

  public onChartClick(event: ECElementEvent | undefined): void {
    if (this.findingSpans() && event?.event) {
      const spanId = this.findingSpansChart.select(this.chartInstance, event);

      if (spanId) {
        this.pointSelected.emit(spanId);
      }

      return;
    }

    if (typeof event?.dataIndex !== 'number' || !event?.event) {
      this.selectedPointDataIndex = null;

      return;
    }

    const { dataIndex, name } = event;
    this.selectedPointDataIndex = toDotIndex(dataIndex, this.pairMode());

    const clickedDataId = name;

    if (clickedDataId) {
      this.pointSelected.emit(clickedDataId);
    }
  }

  public selectNextPoint(): void {
    if (this.findingSpans()) {
      this.selectNextFindingSpan();

      return;
    }

    if (!this.chartInstance || this.processedDotGraphData().length === 0) {
      return;
    }

    const nextIndex: number = this.calculateNextIndex();
    this.selectedPointDataIndex = nextIndex;

    const selectedId = this.processedDotGraphData()[nextIndex].id;
    this.pointSelected.emit(selectedId);

    const selectedTimestamp = this.processedDotGraphData()[nextIndex].timestamp;
    this.panDataZoomToTimestamp(selectedTimestamp);

    this.chartInstance.dispatchAction({
      dataIndex: toSeriesDataIndex(nextIndex, this.pairMode()),
      seriesIndex: 1,
      type: 'showTip',
    });
  }

  public onChartFinished(): void {
    this.chartFinished.set(true);
  }

  private selectNextFindingSpan(): void {
    if (!this.chartInstance) {
      return;
    }

    const step = this.findingSpansChart.selectNext();

    if (!step) {
      return;
    }

    this.pointSelected.emit(step.span.id);
    this.panDataZoomToTimestamp(step.span.start);
    this.chartInstance.dispatchAction({ dataIndex: step.dataIndex, seriesIndex: step.seriesIndex, type: 'showTip' });
  }

  private initializeChart(): void {
    this.calculateDataRange();

    const findingSpans = this.findingSpans();
    const newOptions: EChartsOption = {
      dataZoom: this.getDataZoomConfig(),
      grid: this.getGridConfig(),
      ...(findingSpans?.items.length ? { legend: getFindingSpansLegend(findingSpans.legend) } : {}),
      series: this.getSeriesConfig(),
      title: {
        left: 0,
        text: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.graph.viewerCountOverTime'),
        textStyle: {
          color: ColorUtilitiesService.shikensoPurpleBase,
          fontSize: 12,
          fontWeight: 'normal',
        },
        top: 0,
      },
      tooltip: {
        axisPointer: {
          lineStyle: {
            color: ColorUtilitiesService.shikensoPinkBase,
            type: 'dashed',
          },
          type: 'line',
        },
        formatter: (params: unknown): string => {
          if (!Array.isArray(params) || params.length === 0) {
            return '';
          }

          const param = params[0] as { value: [number, number]; axisValue?: unknown };

          if (
            !param ||
            !param.value ||
            !Array.isArray(param.value) ||
            param.value.length < 2 ||
            typeof param.value[0] !== 'number' ||
            typeof param.value[1] !== 'number'
          ) {
            return '';
          }

          const [timestamp, value] = param.value;

          const timeStr: string = this.dateUtils.formatDate(new Date(timestamp), 'HH:mm');
          const valueStr: string = this.numberUtils.getNumberWithSuffix(value);

          return this.findingSpansChart.prefixTooltip(param.axisValue, `${timeStr} - ${valueStr} ${this.viewersLabel}`);
        },
        trigger: 'axis',
      },
      xAxis: this.getXAxisConfig(),
      yAxis: this.getYAxisConfig(),
    };

    this.chartOption.set(newOptions);
  }

  private setupChartClickListener(chart: EChartsType): void {
    chart.getZr().on('click', (params: IChartClickParams) => {
      const pointInPixel = [params.offsetX, params.offsetY];

      // ECharts' own click handler always runs after this one (zrEventfulCallAtLast), so a finding label drawn
      // above the grid is cleared here first and then selected again by onChartClick.
      if (!chart.containPixel('grid', pointInPixel)) {
        this.selectedPointDataIndex = null;
        this.findingSpansChart.clearSelection();
      }
    });
  }

  private calculateDataRange(): void {
    const data = this.processedViewerGraphData() || this.processedDotGraphData();

    if (!data?.length) {
      const now = Date.now();
      this.minTimestamp = subHours(now, 1).getTime();
      this.maxTimestamp = addHours(now, 1).getTime();
      this.minValue = 0;
      this.maxValue = 100;

      return;
    }

    let minTs = data[0].timestamp;
    let maxTs = data[0].timestamp;
    let minVal = data[0].value;
    let maxVal = data[0].value;

    for (const item of data) {
      if (item.timestamp < minTs) minTs = item.timestamp;

      if (item.timestamp > maxTs) maxTs = item.timestamp;

      if (item.value < minVal) minVal = item.value;

      if (item.value > maxVal) maxVal = item.value;
    }

    const valuePadding = (maxVal - minVal) * 0.1;

    this.minTimestamp = minTs - 1;
    this.maxTimestamp = maxTs;
    this.minValue = 0;
    this.maxValue = maxVal + valuePadding;
  }

  private getGridConfig(): EChartsOption['grid'] {
    return {
      bottom: 72,
      containLabel: false,
      left: '6%',
      right: '2%',
      top: 48,
    };
  }

  private getDataZoomConfig(): DataZoomComponentOption[] {
    const tenMinutesInMilliseconds = 10 * 60 * 1000;

    return [
      {
        backgroundColor: 'transparent',
        borderColor: ColorUtilitiesService.shikensoGray40012,
        bottom: 10,
        dataBackground: {
          areaStyle: {
            color: ColorUtilitiesService.shikensoGray40040,
          },
          lineStyle: {
            color: ColorUtilitiesService.shikensoPinkBase40,
          },
        },
        fillerColor: ColorUtilitiesService.shikensoGray40012,
        filterMode: 'none',
        handleStyle: {
          borderColor: ColorUtilitiesService.shikensoGray400,
          color: ColorUtilitiesService.shikensoGray400,
        },
        height: 20,
        minValueSpan: tenMinutesInMilliseconds,
        moveHandleSize: 6,
        moveHandleStyle: {
          color: ColorUtilitiesService.shikensoGray400,
        },
        selectedDataBackground: {
          areaStyle: {
            color: ColorUtilitiesService.shikensoGray40060,
          },
          lineStyle: {
            color: ColorUtilitiesService.shikensoPinkBase40,
          },
        },
        show: true,
        showDataShadow: true,
        showDetail: false,
        type: 'slider',
        xAxisIndex: [0],
        zoomLock: false,
      },
    ];
  }

  private getXAxisConfig(): EChartsOption['xAxis'] {
    return {
      axisLabel: {
        color: ColorUtilitiesService.shikensoPurpleBase60,
        fontSize: 10,
        formatter: '{HH}:{mm}',
        margin: 20,
        showMinLabel: true,
      },
      axisLine: {
        show: false,
      },
      axisTick: {
        alignWithLabel: true,
        length: 12,
        lineStyle: {
          color: ColorUtilitiesService.shikensoGray600,
        },
        show: true,
      },
      boundaryGap: false,
      max: this.maxTimestamp,
      min: this.minTimestamp,
      splitLine: {
        show: false,
      },
      type: 'time',
    } as EChartsOption['xAxis'];
  }

  private getYAxisConfig(): EChartsOption['yAxis'] {
    return {
      axisLabel: {
        color: ColorUtilitiesService.shikensoPurpleBase60,
        fontSize: 10,
        formatter: (value: number): string => {
          return this.numberUtils.getNumberWithSuffix(value);
        },
        margin: 10,
      },
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      max: this.maxValue,
      min: this.minValue,
      name: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.graph.viewersTitle'),
      nameGap: 40,
      nameLocation: 'middle',
      nameTextStyle: {
        color: ColorUtilitiesService.shikensoPurpleBase,
        fontSize: 10,
        fontWeight: 'normal',
      },
      splitLine: {
        lineStyle: {
          color: ColorUtilitiesService.shikensoGrayBase,
          type: 'dashed',
        },
        show: true,
      },
      type: 'value',
    };
  }

  private getSeriesConfig(): SeriesOption[] {
    const dotGraphData = this.processedDotGraphData();
    const viewerGraphData = this.processedViewerGraphData();
    const isPairMode = this.pairMode();

    const dotSeriesData = this.buildSeriesData(dotGraphData, isPairMode);
    const viewerSeriesData = this.buildSeriesData(viewerGraphData, false);
    const seriesLineStyle = this.getSeriesLineStyle(isPairMode);
    const markLineConfig = this.getMarkLineConfig();
    const dotConfiguration = this.graphUtilitiesService.getDotConfiguration();
    const viewerSeries: LineSeriesOption = {
      areaStyle: {
        color: ColorUtilitiesService.shikensoGray40012,
      },
      data: viewerSeriesData,
      emphasis: {
        disabled: true,
      },
      itemStyle: {
        color: ColorUtilitiesService.shikensoPinkBase,
      },
      lineStyle: {
        color: ColorUtilitiesService.shikensoPinkBase40,
      },
      markLine: viewerSeriesData?.length ? markLineConfig : undefined,
      symbolSize: 0,
      type: 'line',
    };
    const findingSpans = this.findingSpans();

    if (findingSpans) {
      const viewerPoints: TGraphPoint[] = viewerGraphData.map((item) => [item.timestamp, item.value]);
      const spans = sortFindingSpans(findingSpans.items);

      return [
        viewerSeries,
        ...this.findingSpansChart.buildSeries(
          spans,
          spans.map((span) => getCurveSegment(viewerPoints, span.start, span.end)),
          this.graphUtilitiesService.getColorByStatus(findingSpans.status),
          findingSpans.legend,
          1,
        ),
      ];
    }

    return [
      viewerSeries,
      {
        data: dotSeriesData.map((item) => ({
          ...item,
          itemStyle: {
            borderColor: ColorUtilitiesService.shikensoBaseWhite,
            borderWidth: dotConfiguration.borderWidth,
            color: this.graphUtilitiesService.getColorByStatus(item?.status ?? EAnalyticsStatus.ok),
            shadowBlur: dotConfiguration.shadowBlur,
            shadowColor: `${this.graphUtilitiesService.getColorByStatus(item?.status ?? EAnalyticsStatus.ok)}24`,
          },
        })),
        lineStyle: seriesLineStyle,
        name: 'LineData',
        showSymbol: true,
        symbol: 'circle',
        symbolSize: dotConfiguration.symbolSize,
        type: 'line',
      } as LineSeriesOption,
    ];
  }

  private buildSeriesData(data: ILineGraphData[], isPairMode: boolean): (TLineGraphSeriesItem | null)[] {
    if (!data?.length) {
      return [];
    }

    if (!isPairMode) {
      return this.buildStandardSeriesData(data);
    } else {
      return this.buildPairSeriesData(data);
    }
  }

  private buildStandardSeriesData(data: ILineGraphData[]): TLineGraphSeriesItem[] {
    const seriesData: TLineGraphSeriesItem[] = [];

    for (const item of data) {
      seriesData.push({ name: item.id, status: item.status, value: [item.timestamp, item.value] });
    }

    return seriesData;
  }

  private buildPairSeriesData(data: ILineGraphData[]): (TLineGraphSeriesItem | null)[] {
    const seriesData: (TLineGraphSeriesItem | null)[] = [];

    for (let i = 0; i < data.length; i += 2) {
      const point1 = data[i];
      const point2 = data[i + 1];

      if (!point2) {
        break;
      }

      seriesData.push({
        name: point1.id,
        status: point1.status,
        value: [point1.timestamp, point1.value],
      });
      seriesData.push({
        name: point2.id,
        status: point2.status,
        value: [point2.timestamp, point2.value],
      });

      if (i < data.length - 2) {
        seriesData.push(null);
      }
    }

    return seriesData;
  }

  private getSeriesLineStyle(isPairMode: boolean): LineSeriesOption['lineStyle'] {
    if (!isPairMode) {
      return { width: 0 };
    } else {
      return { color: ColorUtilitiesService.shikensoPinkBase, type: 'dashed', width: 2 };
    }
  }

  private getMarkLineConfig(): LineSeriesOption['markLine'] {
    const markLineData: TMarkLineDataItem[] = [];

    if (this.minTimestamp && this.maxTimestamp) {
      let currentDayStart = startOfDay(new Date(this.minTimestamp));

      if (currentDayStart.getTime() < this.minTimestamp) {
        currentDayStart = addDays(currentDayStart, 1);
      }

      while (currentDayStart.getTime() <= this.maxTimestamp) {
        markLineData.push({
          label: {
            color: ColorUtilitiesService.shikensoGray700,
            fontSize: 10,
            formatter: this.dateUtils.formatDate(currentDayStart, 'MMM d'),
            padding: [2, 0, 0, 0],
          },
          lineStyle: {
            color: ColorUtilitiesService.shikensoGray400,
            type: 'dashed',
            width: 1,
          },
          xAxis: currentDayStart.getTime(),
        });
        currentDayStart = addDays(currentDayStart, 1);
      }
    }

    return {
      data: markLineData,
      label: {
        show: true,
      },
      silent: true,
      symbol: ['none', 'none'],
    };
  }

  private calculateNextIndex(): number {
    return getNextDotIndex(this.selectedPointDataIndex, this.processedDotGraphData().length);
  }

  private panDataZoomToTimestamp(timestamp: number): void {
    if (!this.chartInstance) {
      return;
    }

    const currentOption = this.chartInstance.getOption() as EChartsOption;

    if (currentOption.dataZoom && Array.isArray(currentOption.dataZoom) && currentOption.dataZoom.length > 0) {
      const [dataZoomConfig] = currentOption.dataZoom;
      const currentStartPercent = typeof dataZoomConfig.start === 'number' ? dataZoomConfig.start : 0;
      const currentEndPercent = typeof dataZoomConfig.end === 'number' ? dataZoomConfig.end : 100;
      const currentSpanPercent = currentEndPercent - currentStartPercent;

      const totalDuration = this.maxTimestamp - this.minTimestamp;
      const currentStartTimestamp = this.minTimestamp + totalDuration * (currentStartPercent / 100);
      const currentEndTimestamp = this.minTimestamp + totalDuration * (currentEndPercent / 100);

      let newStartPercent: number | null = null;

      if (timestamp < currentStartTimestamp) {
        newStartPercent = ((timestamp - this.minTimestamp) / totalDuration) * 100 - currentSpanPercent * 0.1;
      } else if (timestamp > currentEndTimestamp) {
        newStartPercent = ((timestamp - this.minTimestamp) / totalDuration) * 100 - currentSpanPercent * 0.9;
      }

      if (newStartPercent !== null) {
        newStartPercent = Math.max(0, newStartPercent);
        let newEndPercent = newStartPercent + currentSpanPercent;

        if (newEndPercent > 100) {
          newEndPercent = 100;
          newStartPercent = Math.max(0, 100 - currentSpanPercent);
        }

        this.chartInstance.dispatchAction({
          dataZoomIndex: 0,
          end: newEndPercent,
          start: newStartPercent,
          type: 'dataZoom',
        });
      }
    }
  }
}
